/* CENEC-branded PDF export for the BESS frequency-regulation summary.
 *
 * Built client-side with jsPDF + autoTable so no server-side headless browser
 * is needed. Brand fonts (Space Grotesk headings, Source Sans 3 body) are
 * fetched from /fonts at export time and embedded into the document, so the
 * output matches the CENEC document standard rather than a PDF core font.
 *
 * Save location: the File System Access API picker is opened FIRST, while the
 * click still carries user activation. Building the PDF afterwards would
 * otherwise consume the activation window and Chrome would reject the picker.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// CENEC brand tokens — mirrors templates/paged/cenec-paged.css
const NAVY       = [26, 26, 46];     // --cenec-dark        #1a1a2e
const NAVY_ALT   = [22, 33, 62];     // --cenec-dark-alt    #16213e
const GREEN      = [78, 204, 163];   // --cenec-green       #4ecca3
const GREEN_DARK = [56, 166, 126];   // --cenec-green-dark  #38a67e
const INK        = [45, 52, 54];     // --text-primary      #2d3436
const INK_SOFT   = [74, 85, 104];    // --text-secondary    #4a5568
const MUTED      = [138, 154, 149];  // --text-muted        #8a9a95
const OFF_WHITE  = [240, 244, 243];  // --off-white         #f0f4f3
const RULE       = [224, 230, 228];  // --gray-light        #e0e6e4

const HEAD = 'SpaceGrotesk';
const BODY = 'SourceSans';

const PAGE_W  = 210;
const PAGE_H  = 297;
const MARGIN  = 16;
const CONTENT = PAGE_W - MARGIN * 2;

const FONTS = [
  { file: 'SpaceGrotesk-Medium.ttf',   family: HEAD, style: 'normal' },
  { file: 'SpaceGrotesk-Bold.ttf',     family: HEAD, style: 'bold'   },
  { file: 'SourceSans3-Regular.ttf',   family: BODY, style: 'normal' },
  { file: 'SourceSans3-SemiBold.ttf',  family: BODY, style: 'bold'   },
];

// ---------------------------------------------------------------------------
// formatting
// ---------------------------------------------------------------------------

/** Energy with a scale-appropriate unit. Mirrors fmtKwh in the view. */
function kwh(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)} GWh`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(2)} MWh`;
  return `${v.toFixed(1)} kWh`;
}

function num(v, dp = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function pct(v, dp = 2) {
  return v === null || v === undefined || Number.isNaN(v) ? '—' : `${v.toFixed(dp)} %`;
}

function stamp(iso, withTime = true) {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = n => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return withTime ? `${date} ${p(d.getHours())}:${p(d.getMinutes())}` : date;
}

// ---------------------------------------------------------------------------
// asset loading
// ---------------------------------------------------------------------------

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Chunked — String.fromCharCode blows the argument limit on a 230 KB font.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return window.btoa(binary);
}

async function embedFonts(doc) {
  const loaded = await Promise.all(FONTS.map(async f => {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/fonts/${f.file}`);
    if (!res.ok) throw new Error(`font ${f.file}: HTTP ${res.status}`);
    return { ...f, b64: toBase64(await res.arrayBuffer()) };
  }));
  loaded.forEach(f => {
    doc.addFileToVFS(f.file, f.b64);
    doc.addFont(f.file, f.family, f.style);
  });
}

/** Rasterise the brand SVG so jsPDF can place it. Same-origin, so the canvas
 *  stays untainted and toDataURL works. Returns null if anything fails —
 *  the header falls back to a text wordmark. */
async function loadLogo() {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/cenec-logo.svg`);
    if (!res.ok) return null;
    const svg = await res.text();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    const img = new Image();
    img.src = url;
    await img.decode();
    const scale = 4;                       // 4× for crisp print
    const w = 958, h = Math.round(958 / (958.34 / 342.6));
    const canvas = document.createElement('canvas');
    canvas.width = w * scale / 2;
    canvas.height = h * scale / 2;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    return { data: canvas.toDataURL('image/png'), ratio: 958.34 / 342.6 };
  } catch (e) {
    return null;
  }
}

// ---------------------------------------------------------------------------
// chrome: header band, footer
// ---------------------------------------------------------------------------

function drawHeader(doc, logo) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE_W, 28, 'F');
  doc.setFillColor(...GREEN);
  doc.rect(0, 28, PAGE_W, 1.2, 'F');

  if (logo) {
    const h = 11;
    doc.addImage(logo.data, 'PNG', MARGIN, 8.5, h * logo.ratio, h);
  } else {
    doc.setFont(HEAD, 'bold');
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text('CENEC', MARGIN, 18);
  }

  doc.setFont(BODY, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GREEN);
  doc.text('NETWORK RESOURCE GRID', PAGE_W - MARGIN, 13.5, { align: 'right' });
  doc.setTextColor(220, 226, 224);
  doc.text('BESS frequency-regulation model', PAGE_W - MARGIN, 19, { align: 'right' });
}

function drawFooters(doc, reference, generatedAt) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, PAGE_H - 14, PAGE_W - MARGIN, PAGE_H - 14);
    doc.setFont(BODY, 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(reference, MARGIN, PAGE_H - 9.5);
    doc.text(`Generated ${generatedAt}`, PAGE_W / 2, PAGE_H - 9.5, { align: 'center' });
    doc.text(`Page ${i} of ${pages}`, PAGE_W - MARGIN, PAGE_H - 9.5, { align: 'right' });
  }
}

/** Muted explanatory paragraph. Measures its own wrapping so the caller
 *  advances by the real height instead of a guess. */
function note(doc, text, y) {
  doc.setFont(BODY, 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  const lines = doc.splitTextToSize(text, CONTENT);
  doc.text(lines, MARGIN, y);
  return y + lines.length * 3.1;
}

function sectionTitle(doc, text, y) {
  doc.setFont(HEAD, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY_ALT);
  doc.text(text, MARGIN, y);
  // Full-width hairline, clear of the descenders — a short rule tucked under
  // the baseline reads as an underline of the first word, not a section rule.
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y + 3.4, PAGE_W - MARGIN, y + 3.4);
  return y + 9;
}

/** Head cell right-aligned to sit over a right-aligned figure column.
 *  headStyles otherwise wins over columnStyles for the head row. */
const rh = content => ({ content, styles: { halign: 'right' } });

const makeTableBase = (doc, logo) => ({
  theme: 'plain',
  margin: { left: MARGIN, right: MARGIN, top: 36, bottom: 20 },
  // Any table may spill; a continuation page must still carry the brand band.
  didDrawPage: data => { if (data.pageNumber > 1) drawHeader(doc, logo); },
  styles: {
    font: BODY, fontSize: 8.5, cellPadding: { top: 2, bottom: 2, left: 2.5, right: 2.5 },
    textColor: INK, lineColor: RULE, lineWidth: 0,
  },
  headStyles: {
    font: HEAD, fontStyle: 'bold', fontSize: 8, textColor: [255, 255, 255],
    fillColor: NAVY_ALT, lineWidth: 0,
  },
  alternateRowStyles: { fillColor: OFF_WHITE },
});

// ---------------------------------------------------------------------------
// document body
// ---------------------------------------------------------------------------

function buildDocument(doc, summary, logo, generatedAt) {
  const { totals, cycles, meta, periods, period } = summary;
  const cfg = meta.config;
  const tableBase = makeTableBase(doc, logo);

  drawHeader(doc, logo);

  let y = 42;
  doc.setFont(HEAD, 'bold');
  doc.setFontSize(19);
  doc.setTextColor(...NAVY);
  doc.text('Battery Frequency-Regulation Summary', MARGIN, y);

  y += 7;
  doc.setFont(BODY, 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...INK_SOFT);
  const site = (meta.source_site || '—').replace(/\b\w/g, c => c.toUpperCase());
  doc.text(
    `${site} grid frequency · ${stamp(meta.first_ts)} → ${stamp(meta.last_ts)} · `
    + `${num(cycles.span_days, 1)} days · ${(cycles.samples || 0).toLocaleString('en-GB')} samples`,
    MARGIN, y, { maxWidth: CONTENT },
  );

  // --- headline figures ----------------------------------------------------
  y += 8;
  const cards = [
    ['Energy injected',   kwh(totals.freqreg_grid_out_kwh), 'frequency duty'],
    ['Energy absorbed',   kwh(totals.freqreg_grid_in_kwh),  'frequency duty'],
    ['Round-trip loss',   kwh(totals.roundtrip_loss_kwh),   pct(totals.roundtrip_efficiency_pct, 1) + ' efficiency'],
    ['Equivalent cycles', num(cycles.total, 1),             `${num(cycles.per_day, 2)} / day`],
  ];
  const cardW = (CONTENT - 3 * 3) / 4;
  cards.forEach(([label, value, note], i) => {
    const x = MARGIN + i * (cardW + 3);
    doc.setFillColor(...OFF_WHITE);
    doc.rect(x, y, cardW, 20, 'F');
    doc.setFillColor(...GREEN);
    doc.rect(x, y, 0.9, 20, 'F');
    doc.setFont(BODY, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(label.toUpperCase(), x + 3, y + 5);
    doc.setFont(HEAD, 'bold');
    doc.setFontSize(11.5);
    doc.setTextColor(...NAVY);
    doc.text(value, x + 3, y + 12);
    doc.setFont(BODY, 'normal');
    doc.setFontSize(6.8);
    doc.setTextColor(...MUTED);
    doc.text(note, x + 3, y + 16.8);
  });
  y += 28;

  // --- energy balance ------------------------------------------------------
  y = sectionTitle(doc, 'Energy balance over the simulated window', y);
  autoTable(doc, {
    ...tableBase,
    startY: y,
    head: [['Flow', 'Direction', rh('Energy')]],
    body: [
      ['Frequency response — absorbed',  'grid → BESS', kwh(totals.freqreg_grid_in_kwh)],
      ['Frequency response — injected',  'BESS → grid', kwh(totals.freqreg_grid_out_kwh)],
      ['SOC recovery feed (recharge)',   'grid → BESS', kwh(totals.external_in_kwh)],
      ['SOC recovery bleed',             'BESS → grid', kwh(totals.external_out_kwh)],
      ['Total grid import',              'grid → BESS', kwh(totals.grid_in_kwh)],
      ['Total grid export',              'BESS → grid', kwh(totals.grid_out_kwh)],
      ['Net energy stored (ΔSOC)',       'in cells',    kwh(totals.soc_delta_kwh)],
      ['Conversion losses',              'charge + discharge', kwh(totals.conversion_loss_kwh)],
      ['SOC-clipping losses',            'refused at 0 / 100 %', kwh(totals.clipping_loss_kwh)],
      ['Total round-trip losses',        'dissipated + refused', kwh(totals.roundtrip_loss_kwh)],
      ['Round-trip efficiency, measured', 'export ÷ cycled import', pct(totals.roundtrip_efficiency_pct)],
      ['Round-trip efficiency, nominal',  'charge η × discharge η', pct(totals.nominal_roundtrip_pct)],
    ],
    columnStyles: {
      0: { cellWidth: 74 },
      1: { cellWidth: 50, textColor: MUTED },
      2: { halign: 'right', fontStyle: 'bold' },
    },
    didParseCell: d => {
      if (d.section !== 'body') return;
      const i = d.row.index;
      if (i === 4 || i === 5 || i === 9) d.cell.styles.textColor = NAVY_ALT;
      if (i >= 7 && i <= 9 && d.column.index === 2) d.cell.styles.textColor = GREEN_DARK;
      if (i === 9) d.cell.styles.fontStyle = 'bold';
    },
  });
  y = note(
    doc,
    'Losses are measured, not assumed: import − export − ΔSOC over the window. The measured '
    + 'efficiency sits below nominal because the model clamps SOC at 0 and at capacity — energy '
    + 'offered to a full battery, or drawn from an empty one, is refused rather than converted. '
    + 'That refused energy is the SOC-clipping line, and it is curtailed service, not a cell property.',
    doc.lastAutoTable.finalY + 5,
  ) + 6;

  // --- cycling -------------------------------------------------------------
  y = sectionTitle(doc, 'Cycling', y);
  autoTable(doc, {
    ...tableBase,
    startY: y,
    head: [['Equivalent full cycles', rh('Total duty'), rh('Frequency response only')]],
    body: [
      ['Over the simulated window', num(cycles.total, 2),       num(cycles.freqreg_total, 2)],
      ['Average per day',           num(cycles.per_day, 3),     num(cycles.freqreg_per_day, 3)],
      ['Average per week',          num(cycles.per_week, 2),    num(cycles.freqreg_per_week, 2)],
      ['Average per month',         num(cycles.per_month, 2),   num(cycles.freqreg_per_month, 2)],
      ['Annualised',                num(cycles.per_year, 1),    num(cycles.freqreg_per_year, 1)],
    ],
    columnStyles: {
      0: { cellWidth: 78 },
      1: { halign: 'right', fontStyle: 'bold' },
      2: { halign: 'right' },
    },
  });
  note(
    doc,
    `One equivalent full cycle = ${kwh(cycles.capacity_kwh)} of energy leaving the cells. `
    + '"Total duty" includes the SOC-recovery bleed and feed; "frequency response only" isolates '
    + 'the regulation duty, so the difference is the cycling cost of keeping the battery in its SOC band. '
    + 'Rates are the window total scaled linearly (month = 30.44 days, year = 365.25 days).',
    doc.lastAutoTable.finalY + 5,
  );

  // --- configuration (page 2, above the breakdown) -------------------------
  // The response curve is what the run actually followed; the dead-band row
  // above is its first breakpoint. A run stored before curves existed comes
  // back from the API as its equivalent two-point linear ramp.
  const curve = cfg.response_curve || null;
  const curveMode = curve?.mode || 'linear';
  const curveLow  = [...(curve?.low  || [])].sort((a, b) => b.hz - a.hz);
  const curveHigh = [...(curve?.high || [])].sort((a, b) => a.hz - b.hz);

  doc.addPage();
  drawHeader(doc, logo);
  y = sectionTitle(doc, 'Model configuration', 42);
  autoTable(doc, {
    ...tableBase,
    startY: y,
    head: [['Parameter', 'Value', 'Parameter', 'Value']],
    body: [
      ['Max power',            `${num(cfg.max_power_kw / 1000, 1)} MW`,   'Dead-band',        `${num(cfg.deadband_low_hz, 2)} – ${num(cfg.deadband_high_hz, 2)} Hz`],
      ['Duration',             `${num(cfg.duration_hours, 1)} h`,          'Response shape',   curveMode === 'step' ? 'Discrete steps' : 'Proportional'],
      ['Capacity',             kwh(cfg.capacity_kwh),                      'Charge efficiency', num(cfg.eff_charge, 2)],
      ['Lower SOC threshold',  `${num(cfg.low_threshold_pct, 0)} %`,       'Discharge efficiency', num(cfg.eff_discharge, 2)],
      ['Upper SOC threshold',  `${num(cfg.high_threshold_pct, 0)} %`,      'Initial SOC',      `${num(cfg.initial_soc_pct, 0)} %`],
      ['External recharge',    `${num(cfg.ext_low_charge_kw / 1000, 1)} MW`, 'External bleed', `${num(cfg.ext_high_discharge_kw / 1000, 1)} MW`],
    ],
    columnStyles: {
      0: { cellWidth: 40, textColor: INK_SOFT },
      1: { cellWidth: 45, fontStyle: 'bold' },
      2: { cellWidth: 45, textColor: INK_SOFT },
      3: { fontStyle: 'bold' },
    },
  });

  // --- response curve ------------------------------------------------------
  let afterConfigY = doc.lastAutoTable.finalY;
  if (curveLow.length && curveHigh.length) {
    const rows = [];
    const n = Math.max(curveLow.length, curveHigh.length);
    for (let i = 0; i < n; i++) {
      const lo = curveLow[i];
      const hi = curveHigh[i];
      rows.push([
        lo ? `${num(lo.hz, 3)} Hz` : '—',
        lo ? `${num(lo.pct, 0)} %` : '—',
        hi ? `${num(hi.hz, 3)} Hz` : '—',
        hi ? `${num(hi.pct, 0)} %` : '—',
      ]);
    }
    let ry = sectionTitle(doc, 'Frequency response curve', doc.lastAutoTable.finalY + 8);
    autoTable(doc, {
      ...tableBase,
      startY: ry,
      head: [['Under-frequency', 'Inject', 'Over-frequency', 'Absorb']],
      body: rows,
      columnStyles: {
        0: { cellWidth: 42, textColor: INK_SOFT },
        1: { cellWidth: 43, fontStyle: 'bold' },
        2: { cellWidth: 45, textColor: INK_SOFT },
        3: { fontStyle: 'bold' },
      },
    });
    afterConfigY = note(
      doc,
      curveMode === 'step'
        ? 'Output is held at each breakpoint until the next one is reached, and at the outermost '
          + 'value beyond it — a relay-style response that switches in fixed blocks. The first row '
          + 'of each side is the dead-band edge, where output is zero.'
        : 'Output ramps between breakpoints, reaching each percentage exactly at its frequency, and '
          + 'holds at the outermost value beyond them. Each segment is therefore a droop of its own. '
          + 'The first row of each side is the dead-band edge, where output is zero.',
      doc.lastAutoTable.finalY + 5,
    );
  }

  // --- basis / caveats -----------------------------------------------------
  let cy = afterConfigY + 8;
  const dq = summary.data_quality;
  if (dq && (dq.clamped_steps > 0 || dq.legacy_rows > 0)) {
    cy = sectionTitle(doc, 'Basis and caveats', cy);
    const text = dq.legacy_rows > 0
      ? `This run predates the per-sample integration fix: its energy was summed at a fixed `
        + `${dq.nominal_step_s} s step against a feed that does not hold that cadence, which `
        + 'over-states every energy total and every cycle count on this page. Re-run the '
        + 'simulation before using these figures.'
      : `Energy is integrated on the real interval between samples — mean `
        + `${num(dq.mean_step_s, 3)} s against a nominal ${dq.nominal_step_s} s, since the upstream `
        + 'feed inserts additional readings that split some nominal intervals. Intervals longer than '
        + `${dq.max_step_s} s are upstream outages rather than service, so they are clamped rather `
        + `than integrated: ${(dq.clamped_steps || 0).toLocaleString('en-GB')} such intervals remove `
        + `${num(dq.unobserved_hours, 1)} h from the ${num(dq.window_hours, 0)} h window, leaving `
        + `${num(dq.coverage_pct, 2)} % coverage. Energy and cycling during that unobserved time are `
        + 'not estimated and are absent from every figure here.';
    cy = note(doc, text, cy) + 6;
  }

  // --- per-period breakdown ------------------------------------------------
  const py = sectionTitle(doc, `Breakdown per ${period}`, cy + 2);

  const slice = period === 'year' ? 4 : period === 'month' ? 7 : 10;
  autoTable(doc, {
    ...tableBase,
    startY: py,
    head: [[
      period === 'year' ? 'Year' : period === 'month' ? 'Month' : 'Day',
      rh('Reg in'), rh('Reg out'), rh('Feed'), rh('Bleed'), rh('Loss'), rh('Cycles'),
      rh('SOC min'), rh('SOC avg'), rh('SOC max'),
    ]],
    body: periods.map(p => [
      p.period_start.slice(0, slice),
      kwh(p.freqreg_grid_in_kwh),
      kwh(p.freqreg_grid_out_kwh),
      kwh(p.external_in_kwh),
      kwh(p.external_out_kwh),
      kwh(p.roundtrip_loss_kwh),
      num(p.cycles_equivalent, 2),
      pct(p.soc_pct_min, 1),
      pct(p.soc_pct_avg, 1),
      pct(p.soc_pct_max, 1),
    ]),
    styles: { ...tableBase.styles, fontSize: 7, cellPadding: { top: 1.4, bottom: 1.4, left: 1.6, right: 1.6 } },
    headStyles: { ...tableBase.headStyles, fontSize: 6.8 },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
      4: { halign: 'right' }, 5: { halign: 'right' },
      6: { halign: 'right', fontStyle: 'bold' },
      7: { halign: 'right', textColor: MUTED }, 8: { halign: 'right', textColor: MUTED }, 9: { halign: 'right', textColor: MUTED },
    },
  });

  const ref = `CENEC · NRG · BESS frequency-regulation summary · run #${meta.config_id}`;
  drawFooters(doc, ref, generatedAt);
}

// ---------------------------------------------------------------------------
// entry point
// ---------------------------------------------------------------------------

/**
 * Build and save the summary PDF.
 * Returns { saved: true, filename } | { cancelled: true } and throws on failure.
 */
export async function exportBatterySummaryPdf(summary) {
  if (!summary || !summary.totals || !summary.cycles || !summary.meta) {
    throw new Error('summary payload incomplete — re-run the simulation');
  }

  const now = new Date();
  const p = n => String(n).padStart(2, '0');
  const dateSlug = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  const site = (summary.meta.source_site || 'site').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const filename = `bess-frequency-summary_${site}_${dateSlug}.pdf`;
  const generatedAt = `${dateSlug} ${p(now.getHours())}:${p(now.getMinutes())}`;

  // Ask where to save first — the picker needs the click's user activation,
  // and embedding ~600 KB of fonts would spend it.
  let handle = null;
  if (window.showSaveFilePicker) {
    try {
      handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
      });
    } catch (err) {
      if (err && err.name === 'AbortError') return { cancelled: true };
      handle = null;   // unsupported or blocked — fall back to a download
    }
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
  await embedFonts(doc);
  const logo = await loadLogo();
  buildDocument(doc, summary, logo, generatedAt);

  const blob = doc.output('blob');

  if (handle) {
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return { saved: true, filename: handle.name || filename };
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { saved: true, filename };
}

export default exportBatterySummaryPdf;
